<template>
  <div
    ref="swiper"
    :class="['slider', { big, arrows, small }]"
    @click="$emit('image-click')"
  >
    <div :class="[{ sections }, { popup }]">
      <template v-if="sections">
        <div
          v-for="(_, indexSections) in filteredItems"
          :key="`sections-${indexSections}`"
          class="section"
          @mouseenter="updateSlide(indexSections)"
        />
      </template>
    </div>
    <div v-if="useSeparateSchemaTag" class="schema-block">
      <template v-for="(image, indexImage) in filteredItems">
        <span
          v-if="image"
          :key="`image-${indexImage}`"
          itemprop="image"
          :content="image"
        />
      </template>
    </div>
    <div
      class="swiper-container swiper effect-slide swiper-container-horizontal"
    >
      <div class="swiper-wrapper">
        <div class="swiper-slide">
          <div :key="`${img}+${name}`" class="content">
            <ui-responsive-image
              draggable="false"
              :src="img || '/static/images/no-img.svg'"
              :image-placeholder="lazyImg ? '/static/images/no-img.svg' : ''"
              :alt="name + (index ? ' – фото ' + index : '')"
              :title="name + (index ? ' – фото ' + index : '')"
              :itemprop="
                !useSeparateSchemaTag && itemprop ? 'image' : undefined
              "
              :mobile-sizes="mobileSizes"
              :lazy="lazyImg"
              :desktop-sizes="desktopSizes"
              :load-original="loadOriginal"
              :adaptive-image="adaptiveImage"
            />
          </div>
        </div>
      </div>
      <div
        class="swiper-pagination swiper-pagination-clickable swiper-pagination-bullets"
      >
        <span
          v-for="(item, i) in filteredItems"
          :key="i"
          :class="{ ' swiper-pagination-bullet-active': index === i }"
          class="swiper-pagination-bullet"
        />
      </div>
      <div v-if="arrows" class="slider_arrows">
        <button class="left" :disabled="!hasPrev" @click="swipePrev">
          <span class="slider_triangle slider_triangle--left">
            <span class="slider_triangle__elem" />
          </span>
        </button>
        <button class="right" :disabled="!hasNext" @click="swipeNext">
          <span class="slider_triangle slider_triangle--right">
            <span class="slider_triangle__elem" />
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ImageSize } from '~/interfaces/app';
import { useSwipe } from '~/composition/useSwipe';
import { useSlider } from '~/composition/useSlider';
import 'assets/style/swiper.min.css';

const props = defineProps({
  arrows: {
    type: Boolean,
    default: false,
  },
  sections: Boolean,
  small: Boolean,
  big: Boolean,
  popup: Boolean,
  value: {
    type: Number,
    default: 0,
  },
  effect: {
    type: String,
    default: 'slide',
    validator: (v: string) =>
      [
        'fade',
        'slide',
        'cube',
        'coverflow',
        'flip',
        'creative',
        'cards',
      ].includes(v),
  },
  speed: {
    type: Number,
    default: 0,
  },
  items: {
    type: Array as () => string[],
    default: () => [],
  },
  lazyImg: Boolean,
  onlyFive: Boolean,
  name: String,
  itemprop: Boolean,
  useSeparateSchemaTag: {
    type: Boolean,
    default: false,
  },
  mobileSizes: {
    type: Object as () => ImageSize,
    default: () => {},
  },
  desktopSizes: {
    type: Object as () => ImageSize,
    default: () => {},
  },
  loadOriginal: {
    type: Boolean,
    default: false,
  },
  adaptiveImage: {
    type: Boolean,
    default: false,
  },
  dynamic: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['image-click', 'input']);
const { value, items, onlyFive } = toRefs(props);
const swipe = useSwipe();
let timerImg: number;
const filteredItems = computed<string[]>(() =>
  onlyFive.value ? items.value.slice(0, 5) : items.value
);

const countSlides = filteredItems.value.length;
const sliderHelper = useSlider(countSlides);

const swiper = ref<any>(null);
const img = ref<string>(filteredItems.value[0]);
const index = ref<number>(0);

watch(value, (newValue) => updateSlide(newValue));

const hasNext = computed<Boolean>(() => !!filteredItems.value[value.value + 1]);
const hasPrev = computed<Boolean>(() => value.value > 0);

const emitChangeSlide = () => {
  emit('input', index.value);
};
const swipeNext = () => {
  index.value = sliderHelper.nextSlide(index.value);
  img.value = filteredItems.value[index.value];
  emitChangeSlide();
};
const swipePrev = () => {
  index.value = sliderHelper.prevSlide(index.value);
  img.value = filteredItems.value[index.value];
  emitChangeSlide();
};

const updateSlide = (i: number) => {
  timerImg = setTimeout(() => {
    clearTimeout(timerImg);
    img.value = filteredItems.value[i];
  }, props.speed);
  index.value = i;
};

onMounted(() => {
  swipe.init(swiper.value);
  swipe.emit.on('swipe-left', (swipeLeft: boolean) => {
    if (swipeLeft) {
      swipeNext();
    } else {
      swipePrev();
    }
  });
  updateSlide(0);
  setTimeout(() => {
    updateSlide(props.value);
  }, 100);
});

onUnmounted(() => {
  clearInterval(timerImg);
  swipe.destroy(swiper.value);
});
</script>
<style lang="scss" src="./SoloSliderScss.scss" scoped></style>
